import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SPORTS, SPORT_IDS } from "@/lib/sports";

const PlayerRow = ({ p, checked, onToggle, testIdPrefix }) => (
  <label
    className={`flex items-center gap-3 bg-[#0A0B0E] border rounded-md p-2 cursor-pointer transition-colors ${
      checked ? "border-[#D4AF37]/60" : "border-white/5 hover:border-white/15"
    }`}
    data-testid={`${testIdPrefix}-${p.slug}`}
  >
    <input
      type="checkbox"
      className="w-4 h-4 accent-[#D4AF37]"
      checked={checked}
      onChange={() => onToggle(p.id)}
    />
    <div className="w-10 h-10 rounded-full overflow-hidden bg-[#111216] shrink-0">
      {p.hero_image_url && (
        <img src={p.hero_image_url} alt={p.name} className="w-full h-full object-cover" />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-display uppercase text-sm font-bold truncate">{p.name}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">
        {[SPORTS[p.sport || "football"]?.label, p.club_name || p.position]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  </label>
);

/**
 * PlayerPicker — sélection de joueurs via barre de recherche.
 * Les joueurs ne s'affichent qu'après une recherche ; les sélectionnés restent visibles.
 */
export default function PlayerPicker({
  players = [],
  selectedIds = [],
  onToggle,
  testIdPrefix = "pick-player",
}) {
  const [query, setQuery] = useState("");
  const [sport, setSport] = useState("all");
  const hasFilter = query.trim().length > 0 || sport !== "all";

  const selectedPlayers = useMemo(
    () => players.filter((p) => selectedIds.includes(p.id)),
    [players, selectedIds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return players.filter((p) => {
      if (selectedIds.includes(p.id)) return false;
      if (sport !== "all" && (p.sport || "football") !== sport) return false;
      if (!q) return true;
      return `${p.name} ${p.club_name || ""} ${p.position || ""}`
        .toLowerCase()
        .includes(q);
    });
  }, [players, query, sport, selectedIds]);

  if (players.length === 0)
    return (
      <p className="text-zinc-500 font-body text-sm" data-testid={`${testIdPrefix}-empty`}>
        Aucun joueur disponible.
      </p>
    );

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un joueur (nom, club)…"
            data-testid={`${testIdPrefix}-search`}
            className="bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 pl-9 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md"
          />
        </div>
        <Select value={sport} onValueChange={setSport}>
          <SelectTrigger
            className="bg-[#0E0F13] border-white/10 text-white rounded-md sm:w-48"
            data-testid={`${testIdPrefix}-sport-filter`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111216] border-white/10 text-white max-h-64">
            <SelectItem value="all">Tous les sports</SelectItem>
            {SPORT_IDS.map((s) => (
              <SelectItem key={s} value={s}>
                {SPORTS[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selectedPlayers.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
            {selectedPlayers.length} sélectionné{selectedPlayers.length > 1 ? "s" : ""}
          </div>
          <div
            className="grid sm:grid-cols-2 gap-2"
            data-testid={`${testIdPrefix}-selected`}
          >
            {selectedPlayers.map((p) => (
              <PlayerRow
                key={p.id}
                p={p}
                checked
                onToggle={onToggle}
                testIdPrefix={testIdPrefix}
              />
            ))}
          </div>
        </>
      )}
      {!hasFilter ? (
        <p
          className="text-zinc-500 font-body text-sm"
          data-testid={`${testIdPrefix}-hint`}
        >
          Utilisez la barre de recherche (ou le filtre sport) pour trouver un
          joueur avec une fiche, puis cochez-le.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 font-body text-sm" data-testid={`${testIdPrefix}-no-result`}>
          Aucun joueur ne correspond à la recherche.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
          {filtered.map((p) => (
            <PlayerRow
              key={p.id}
              p={p}
              checked={selectedIds.includes(p.id)}
              onToggle={onToggle}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      )}
    </div>
  );
}
